import { MRT_ColumnDef } from "material-react-table";
import { EscrowOverdueOrder } from "./types";
import { Chip, IconButton, Link, Stack, Box } from "@mui/material";
import { fMonthDayYearDotNotation } from "../../utils/formatTime";
import { OrderNumberCell } from "../OrderNumberCell";
import { getOrderNumberDisplay, orderNumberFilterFn } from "../ReportsUtils";
import { fCurrency } from "../../utils/formatNumber";
import { useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import Iconify from "../../../minimals/components/iconify";
import { DatePicker } from "@mui/x-date-pickers";
import SingleDateRangeFilter from "./SingleDateRangeFilter";

const DueDateColumn = ({ order }: { order: EscrowOverdueOrder }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editDueDate, setEditDueDate] = useState(dayjs(order.dueDate).format("YYYY-MM-DD"));
  const [isUpdating, setIsUpdating] = useState(false);

  const updateDueDate = async () => {
    setIsUpdating(true);
    await axios.post("/proxy/api/orders/UpdateChecklistTaskDueDate", {
      orderNumber: order.orderNumber,
      taskDescription: "24.",
      dueDate: editDueDate
    });
    //Hack alert! This is not the correct React way to do this, but we need to update the UI
    //and since we're buried so deep here, it would be really difficult to pull this off. This
    //works, but it's a hack!
    order.dueDate = dayjs(editDueDate).toDate();
    setIsUpdating(false);
    setIsEditMode(false);
  };

  const getUI = () => {
    if (!isEditMode) {
      return (
        <>
          <Chip label={fMonthDayYearDotNotation(order.dueDate)} />
          <IconButton sx={{ marginLeft: 1 }} onClick={() => setIsEditMode(true)}>
            <Iconify icon="eva:edit-outline" />
          </IconButton>
        </>
      );
    }
    if (isUpdating) {
      return <h4>Updating....</h4>;
    }
    return (
      <Stack direction="row" alignItems="center">
        <DatePicker
          label="Due Date"
          onChange={(newValue) => setEditDueDate(dayjs(newValue!).format("YYYY-MM-DD"))}
          value={dayjs(editDueDate).toDate()}
          slotProps={{ textField: { sx: { width: 160, marginRight: 2 } } }}
        />
        <Box>
          <IconButton onClick={updateDueDate} color="success">
            <Iconify icon="eva:checkmark-fill" />
          </IconButton>
        </Box>
        <Box>
          <IconButton onClick={() => setIsEditMode(false)} color="error">
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Box>
      </Stack>
    );
  };

  return getUI();
};

export const columns: MRT_ColumnDef<EscrowOverdueOrder>[] = [
  {
    id: "notes",
    header: "",
    enableColumnFilter: false,
    enableColumnActions: false,
    enableColumnDragging: false,
    enableColumnFilterModes: false,
    size: 50,
    enableHiding: false
  },
  {
    header: "Order Number",
    accessorKey: "orderNumber",
    id: "orderNumber",
    accessorFn: getOrderNumberDisplay,
    filterFn: orderNumberFilterFn,
    Cell: OrderNumberCell,
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    header: "Due Date",
    accessorFn: (row) => (row.dueDate ? new Date(row.dueDate) : null),
    filterVariant: "date",
    sortingFn: "datetime",
    Cell: ({ row }) => <DueDateColumn order={row.original} />,
    filterFn: (row, id, filterValue) => {
      if (filterValue && filterValue[0] && filterValue[1] && !row.original.dueDate) {
        return false;
      } else if (filterValue && filterValue[0] && filterValue[1] && row.original.dueDate) {
        const dueDate = new Date(row.original.dueDate).getTime();
        const startDate = new Date(filterValue[0]).getTime();
        const endDate = new Date(filterValue[1]).getTime();

        return dueDate >= startDate && dueDate <= endDate;
      } else {
        return true;
      }
    },
    Filter: SingleDateRangeFilter
  },
  {
    header: "Amount",
    accessorKey: "amount",
    id: "amount",
    Cell: ({ row }) => (row.original.amount ? fCurrency(row.original.amount) : <></>),
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    header: "Closer",
    id: "closerUser",
    accessorFn: (row) => row.closerUser?.fullName || "",
    Cell: ({ renderedCellValue = "", row }) => {
      if (row?.original?.closerUser?.email) {
        return <Link href={`mailto:${row.original.closerUser.email}`}>{renderedCellValue || ""}</Link>;
      }
      return renderedCellValue;
    },
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    header: "Funder",
    id: "funderUser",
    accessorFn: (row) => row.funderUser?.fullName || "",
    filterVariant: "select",
    Cell: ({ renderedCellValue = "", row }) => {
      if (row?.original?.funderUser?.email) {
        <Link href={`mailto:${row.original.funderUser.email}`}>{renderedCellValue}</Link>;
      }
      return renderedCellValue;
    },
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    header: "Post Closer",
    id: "postCloser",
    accessorFn: (row) => row.postCloserUser?.fullName || "",
    Cell: ({ renderedCellValue = "", row }) => {
      if (row?.original?.postCloserUser?.email) {
        return <Link href={`mailto:${row.original.postCloserUser?.email}`}>{renderedCellValue}</Link>;
      }
      return renderedCellValue;
    },
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  },
  {
    header: "Assigned To",
    id: "assignedTo",
    accessorFn: (row) => row.assignedToUser?.fullName || "",
    Cell: ({ renderedCellValue = "", row }) => {
      if (row?.original?.assignedToUser?.email) {
        return <Link href={`mailto:${row.original.assignedToUser.email}`}>{renderedCellValue}</Link>;
      }
      return renderedCellValue;
    },
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  }
];
